import {FC, useEffect, useState} from 'react';
import {useFetchEmployment, type SelfEmploymentProvider} from '@rocket-ui/lk-api';
import {
	ALFA_AGREEMENT_CONFIRM_CAPTION,
	clearAlfaAgreementFrameOpened,
	getSelfEmploymentAgreementLink,
	getSelfEmploymentProvider,
	getSelfEmploymentStatusLabel,
	getWorkStatusOptions,
	getWorkStatusStepTitle,
	hasActiveAlfaSelfEmployment,
	hasConnectedWorkStatus,
	hasRocketworkSelfEmploymentProfile,
	isSelfEmploymentAgreementSent,
	isSelfEmploymentAgreementSigned,
	isWorkStatusConnectedAppearance,
	mapSelfEmploymentProfileConnectionState,
	markAlfaAgreementFrameOpened,
	RegistrationWorkStatus,
	useAlfaAgreementFrameOpened,
	WorkStatusConnectionState,
	WorkStatusConnectionStates,
} from '../../model';
import {isFreeSmzEnabled} from 'shared/lib/isFreeSmz';
import {StepLayout} from 'features/registration';
import {WorkStatusTile} from '../components/WorkStatusTile/WorkStatusTile';
import {WorkStatusesInfoDrawer} from 'features/registration';
import styles from './WorkStatusStep.module.css';

type Props = {
	value: RegistrationWorkStatus | null;
	onChange: (value: RegistrationWorkStatus, provider?: SelfEmploymentProvider) => void;
	allowedStatuses?: RegistrationWorkStatus[];
	connectionStates?: WorkStatusConnectionStates;
	/** false — занятости ещё нет, плитки показывать рано. */
	statusesReady?: boolean;
	title?: string;
	onOpenAgreement?: (url: string, provider?: SelfEmploymentProvider) => void;
	onSelectFreeSmz?: () => void;
	/** true, когда заголовок — «Подключим ещё?». */
	onConnectMoreChange?: (connectMore: boolean) => void;
};

const SELF_EMPLOYMENT_TILE_ORDER: SelfEmploymentProvider[] = ['free_smz', 'rocketwork', 'alfa'];

export const WorkStatusStep: FC<Props> = ({
	value,
	onChange,
	allowedStatuses,
	connectionStates,
	statusesReady = true,
	title,
	onOpenAgreement,
	onSelectFreeSmz,
	onConnectMoreChange,
}) => {
	const {data: employment, isLoading: isEmploymentLoading} = useFetchEmployment();
	const primaryProvider = getSelfEmploymentProvider();
	const freeFrameOpened = useAlfaAgreementFrameOpened('free_smz');
	const rocketFrameOpened = useAlfaAgreementFrameOpened('rocketwork');
	const alfaFrameOpened = useAlfaAgreementFrameOpened('alfa');

	useEffect(() => {
		if (isSelfEmploymentAgreementSigned(employment, 'free_smz')) {
			clearAlfaAgreementFrameOpened('free_smz');
		}

		if (isSelfEmploymentAgreementSigned(employment, 'rocketwork')) {
			clearAlfaAgreementFrameOpened('rocketwork');
		}

		if (isSelfEmploymentAgreementSigned(employment, 'alfa')) {
			clearAlfaAgreementFrameOpened('alfa');
		}
	}, [employment]);

	const workStatusOptions = getWorkStatusOptions();
	const options = allowedStatuses
		? workStatusOptions.filter((option) => allowedStatuses.includes(option.value))
		: workStatusOptions;
	const [isInfoDrawerOpen, setIsInfoDrawerOpen] = useState(false);
	const showFreeSmz = isFreeSmzEnabled();
	const showRocket = hasRocketworkSelfEmploymentProfile(employment);
	const showAlfa = hasActiveAlfaSelfEmployment(employment);
	const showSelfEmployment = options.some((option) => option.value === 'self_employment');

	const profileState = (provider: SelfEmploymentProvider): WorkStatusConnectionState => {
		const profile = employment?.selfEmploymentProfiles?.find((item) => item.provider === provider);

		if (!profile) {
			return 'available';
		}

		return mapSelfEmploymentProfileConnectionState(profile) ?? 'available';
	};

	const frameOpened = (provider: SelfEmploymentProvider): boolean => {
		if (provider === 'free_smz') return freeFrameOpened;
		if (provider === 'rocketwork') return rocketFrameOpened;

		return alfaFrameOpened;
	};

	const visibleSelfEmployment = SELF_EMPLOYMENT_TILE_ORDER.filter((provider) => {
		if (provider === 'free_smz') return showFreeSmz;
		if (provider === 'rocketwork') return showRocket;

		return showAlfa;
	});

	const titleConnections: WorkStatusConnectionStates = {
		...connectionStates,
		self_employment: visibleSelfEmployment
			.map((provider) => profileState(provider))
			.find((state) => isWorkStatusConnectedAppearance(state)),
	};
	const isConnectMore = hasConnectedWorkStatus(titleConnections);
	onConnectMoreChange?.(isConnectMore);
	const stepTitle = title ?? (isConnectMore ? 'Подключим ещё?' : getWorkStatusStepTitle(titleConnections));

	if (!statusesReady || isEmploymentLoading) {
		return (
			<div className={styles.loader} role="status" aria-live="polite" aria-label="Загрузка статусов">
				<span className={styles.spinner} aria-hidden />
			</div>
		);
	}

	return (
		<>
			<StepLayout>
				<StepLayout.Title>{stepTitle}</StepLayout.Title>

				<StepLayout.Content className={styles.tiles}>
					{showSelfEmployment
						? visibleSelfEmployment.map((provider) => {
								const agreementLink = getSelfEmploymentAgreementLink(employment, provider);
								const showSignError = isSelfEmploymentAgreementSent(employment, provider) && frameOpened(provider);
								const mappedState = profileState(provider);
								const connectionState = showSignError ? 'error_sign_agreement' : mappedState;
								const keepAgreementEnabled = Boolean(agreementLink);
								const partnerBadge =
									provider === 'rocketwork' ? 'rocket' : provider === 'alfa' ? 'alfa' : 'none';

								return (
									<WorkStatusTile
										key={provider}
										value="self_employment"
										label={getSelfEmploymentStatusLabel()}
										selected={value === 'self_employment' && provider === primaryProvider}
										partnerBadge={partnerBadge}
										connectionState={connectionState}
										subtitleOverride={
											!showSignError && keepAgreementEnabled ? ALFA_AGREEMENT_CONFIRM_CAPTION : undefined
										}
										disabled={
											!keepAgreementEnabled &&
											(connectionState === 'current' ||
												connectionState === 'pending' ||
												connectionState === 'pending_alfa')
										}
										onSelect={() => {
											if (agreementLink) {
												markAlfaAgreementFrameOpened(provider);
												onOpenAgreement?.(agreementLink, provider);
												return;
											}

											if (provider === 'free_smz') {
												onSelectFreeSmz?.();
												return;
											}

											onChange('self_employment', provider);
										}}
									/>
								);
							})
						: null}
					{options
						.filter((option) => option.value !== 'self_employment')
						.map((option) => {
							const connectionState = connectionStates?.[option.value] ?? 'available';

							return (
								<WorkStatusTile
									key={option.value}
									value={option.value}
									label={option.label}
									selected={value === option.value}
									connectionState={connectionState}
									disabled={
										connectionState === 'current' ||
										connectionState === 'pending' ||
										connectionState === 'pending_alfa'
									}
									onSelect={onChange}
								/>
							);
						})}
				</StepLayout.Content>

				<StepLayout.TextLink type="button" onClick={() => setIsInfoDrawerOpen(true)}>
					Подробнее о статусах
				</StepLayout.TextLink>
			</StepLayout>

			<WorkStatusesInfoDrawer open={isInfoDrawerOpen} onClose={() => setIsInfoDrawerOpen(false)} />
		</>
	);
};
